import {
  complement,
  cond,
  defaultTo,
  identity,
  intersection,
  isEmpty,
  map,
  pathOr,
  pipe,
  prop,
  reduce,
  uniqBy,
} from 'ramda'
import { useSelector } from 'react-redux'

import { SalesGroup } from '~shared-components/api/sales-groups'
import { ENABLED_LANGUAGES } from '~shared-components/components/FlagRender/frozenFlags'
import { useLoggedInUser } from '~shared-components/hooks/useLoggedInUser'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import { SupportedLanguage } from '~shared-components/utils/enums'

import { LanguageType } from '../../../api-clients/api-order-client'

interface Params {
  filterEnabledLanguages?: boolean
  showOnlyCommonLanguage?: boolean
}

const languageCodeMapper = ({ code }: LanguageType) => code as SupportedLanguage

export const getSalesGroupLanguages = (salesGroup: SalesGroup) => {
  return uniqBy<LanguageType, string>(
    prop('code'),
    salesGroup.languages ?? [],
  ).map(languageCodeMapper)
}

const languagesPipeForSalesGroup = pipe(
  map(pathOr([], ['languages'])),
  reduce<LanguageType[], SupportedLanguage[]>(
    (intersected, languages) =>
      intersected.length === 0
        ? languages.map(languageCodeMapper)
        : intersection(intersected, languages.map(languageCodeMapper)),
    [],
  ),
  cond([[complement(isEmpty), identity]]),
  defaultTo([SupportedLanguage.English]),
)

export const useAllowedLanguages = ({
  filterEnabledLanguages = true,
  showOnlyCommonLanguage,
}: Params = {}) => {
  let allowedLanguages: SupportedLanguage[] = []
  const { user } = useLoggedInUser()
  const selectedSalesGroup: SalesGroup | undefined = useSelector(
    LoginSelector.selectedSalesGroup,
  )

  if (showOnlyCommonLanguage) {
    allowedLanguages =
      (user && languagesPipeForSalesGroup(user!.salesGroups!)) ?? []
  } else if (selectedSalesGroup) {
    allowedLanguages = getSalesGroupLanguages(selectedSalesGroup)
  }

  if (filterEnabledLanguages) {
    return allowedLanguages.filter(code => Boolean(ENABLED_LANGUAGES[code]))
  } else {
    return allowedLanguages
  }
}
